/**
 * Data Mapper Utility for Student Profile
 * Transforms API response to match UI data structure
 */

/**
 * Transforms API response to student data format
 * Handles both user_info structure and admission/enrollment structure
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Transformed student data
 */
export const transformStudentData = (apiResponse) => {
    if (!apiResponse) {
        return {};
    }

    // Handle both response structures - new API has user_info at root level
    const userInfo = apiResponse.user_info || apiResponse;
    const parents = userInfo.parents || [];

    // Get parent details - support Father, Mother, and Guardian
    const father = parents.find(p => p.parentType === 'Father') || {};
    const mother = parents.find(p => p.parentType === 'Mother') || {};
    const guardian = parents.find(p => p.parentType === 'Guardian') || {};

    // Extract full name
    const fullName = userInfo.given_name || '';
    const lastName =  userInfo.lastName || '';
    
    // Parse firstName from given_name (e.g., "SHAMMANTH SRI SAI CHARANNKOLAGANI" -> "SHAMMANTH SRI SAI CHARANN")
    let firstName = userInfo.firstName || '';
    if (!firstName && fullName && lastName) {
        firstName = fullName.replace(lastName, '').trim();
    } else if (!firstName && fullName) {
        // If no lastName, take all but last word as firstName
        const nameParts = fullName.split(' ');
        firstName = nameParts.slice(0, -1).join(' ') || fullName;
    }

    return {
        // Personal Information
        firstName: firstName,
        middleName: userInfo.middleName || '',
        lastName: lastName,
        enrollmentId: userInfo.enrollmentId || apiResponse.enrollmentId || '',
        class: formatClassDisplay(userInfo, apiResponse),
        school: userInfo.branchName || '',
        status: userInfo.status || 'Active',
        studentJoinedIn: formatStudentJoinedIn(userInfo),
        
        // Personal Details
        gender: formatGender(userInfo.gender),
        dateOfBirth: parseDateOfBirth(userInfo.dateOfBirth),
        category: (userInfo.categoryName || '').toUpperCase(),
        refAdmNo: userInfo.refAdmNo || '',   
        apptNo: userInfo.apptNo || '',
        admissionDate: parseISODate(userInfo.enrollmentAdmDate),
        registrationDate: parseISODate(userInfo.registrationDate),
        
        // Additional Personal Info
        nationality: userInfo.nationality || '',
        religion: userInfo.religion || '',
        motherTongue: userInfo.motherTongue || '',
        caste: userInfo.caste || '',
        aadharNo: userInfo.aadharNo || '',
        identificationMarks: userInfo.identificationMarks || '',
        
        // Previous School Info
        previousSchool: userInfo.previousSchool || '',
        previousBoard: userInfo.previousBoard || '',
        previousGrade: userInfo.previousGrade || '',
        
        // Contact Information
        pointOfContact: formatPhoneWithCountryCode(userInfo.countryCode, userInfo.contactNo) || userInfo.parentContactNo || '',
        studentMobile: formatPhoneWithCountryCode(userInfo.stdCountryCode, userInfo.stdContactNo) || '',
        studentEmail: userInfo.stdEmailId || userInfo.email || '',
        currentLocation: userInfo.currentLocation || '',
        
        // Father Details
        fatherName: father.parentName || '',
        fatherMobile: formatPhoneWithCountryCode(father.countryCode, father.parentContact),
        fatherEmail: father.parentEmailId || '',
        fatherQualification: father.parentQualification || father.qualification || '',
        fatherOccupation: father.occupation || father.profession || '',
        
        // Mother Details
        motherName: mother.parentName || '',
        motherMobile: formatPhoneWithCountryCode(mother.countryCode, mother.parentContact),
        motherEmail: mother.parentEmailId || '',
        motherQualification: mother.parentQualification || mother.qualification || '',
        motherOccupation: mother.occupation || mother.profession || '',
        
        // Guardian Details (if exists)
        guardianName: guardian.parentName || '',
        guardianMobile: formatPhoneWithCountryCode(guardian.countryCode, guardian.parentContact),
        guardianEmail: guardian.parentEmailId || '',
        guardianQualification: guardian.parentQualification || guardian.qualification || '',
        guardianOccupation: guardian.occupation || guardian.profession || '',
        
        // Emergency Contact
        emergencyContact: userInfo.emergencyContact || formatPhoneWithCountryCode(userInfo.countryCode, userInfo.contactNo) || '',
        contactPersonName: userInfo.contactPersonName || father.parentName || '',
        
        // Academic Information
        studentSegment: userInfo.studentSegment || 'General',
        looking: userInfo.looking || '',
        lastLoginDate: userInfo.lastLoginDate || '',
        studentGroups: formatStudentGroups(userInfo),
        boardName: userInfo['custom:board'] || '',
        gradeName: userInfo['custom:grade'] || '',
        
        // Transport & Other Information
        // Check if transport is assigned from transportInfo or fallback to transportRequired
        transportAvail: (apiResponse.transportInfo?.isTransportAssigned || userInfo.isTransportAssigned === 'ASSIGNED' || userInfo.transportRequired) ? 'Yes' : 'No',
        transportID: userInfo.transportID || '',
        transportRFID: userInfo.transportRFID || '',
        transportStop: userInfo.requestedStop || '',
        staffKid: userInfo.staffKid || 'No',
        sitting: userInfo.sitting || 'No',
        biometricCode: userInfo.biometricCode || '',
        lunchFee: userInfo.lunchFee || 'NO',
        transportActivities: userInfo.transportActivities || '',
        
        // Address Information
        currentAddress: formatAddress(userInfo),
        addressLine: userInfo.addressLine || '',
        cityOrDist: userInfo.cityOrDist || '',
        state: userInfo.state || '',
        pinCode: userInfo.pinCode || '',
        nearestLocality: userInfo.nearestLocality || '',
        
        // Siblings
        siblings: userInfo.siblings || [],
        
        // Additional IDs
        apmaarId: userInfo.apmaarId || '',
        ipinNo: userInfo.ipinNo || '',
        rfid: userInfo.rfid || '',
        
        // Admission Process Info
        knowAboutUs: userInfo.knowAboutUs || '',
        counsellorsName: userInfo.counsellorsName || '',
        remarks: userInfo.remarks || '',
        
        // Student Photo
        studentPhoto: getStudentPhoto(userInfo),
        profilePicUrl: getStudentPhoto(userInfo),
        
        // Section & Class Info
        sectionName: userInfo.sectionName || '',
        branchName: userInfo.branchName || '',
        
        // System fields
        userId: apiResponse.user_id || '',
        orgId: apiResponse.orgId || '',
        academicYear: userInfo.academicYear || apiResponse.academicYear || '',
        isActive: userInfo.status === 'Active',
        username: userInfo.username || '',
        isBaseAccount: apiResponse.isBaseAccount || false,
        
        // Transport Information (from root level of API response)
        transportInfo: apiResponse.transportInfo || null
    };
};

/**
 * Get student photo URL from various possible locations
 */
const getStudentPhoto = (userInfo) => {
    const photoObj = userInfo.studentPhoto;
    return photoObj?.storageLocation || '';
};

/**
 * Format phone number with country code
 * @param {string} countryCode - Country code (e.g., "91")
 * @param {string} phoneNumber - Phone number without country code
 * @returns {string} Formatted phone number with country code prefix
 */
const formatPhoneWithCountryCode = (countryCode, phoneNumber) => {
    if (!phoneNumber) return '';
    
    // If phoneNumber already has country code prefix, return as is
    if (phoneNumber.startsWith('+')) {
        return phoneNumber;
    }
    
    // If countryCode is provided, prepend it with +
    if (countryCode) {
        return `+${countryCode} ${phoneNumber}`;
    }
    
    // Return phone number as is
    return phoneNumber;
};

/**
 * Format phone number - remove country code prefix
 */
const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    return phone.toString().replace(/^\+?\d{1,3}/, '').trim();
};

/**
 * Format class display with section
 */
const formatClassDisplay = (userInfo, apiResponse) => {
    const grade = userInfo['custom:grade'] || '';
    const section = userInfo.sectionName || '';
    if (grade && section) {
        return `${grade}/${section}`;
    }
    return grade || section || '';
};

/**
 * Format student joined information
 */
const formatStudentJoinedIn = (userInfo) => {
    const grade = userInfo['custom:grade'] || '';
    return grade ? `${grade} (Current Year)` : '';
};

/**
 * Format gender display
 */
const formatGender = (gender) => {
    if (!gender) return '';
    const genderLower = gender.toLowerCase();
    if (genderLower === 'male' || genderLower === 'm') return 'Male';
    if (genderLower === 'female' || genderLower === 'f') return 'Female';
    return gender;
};

/**
 * Parse date of birth from various formats
 */
const parseDateOfBirth = (dob) => {
    if (!dob) return null;
    
    // Handle MongoDB $date object
    if (dob.$date) {
        const date = new Date(dob.$date);
        return isNaN(date.getTime()) ? null : date;
    }
    
    // Handle "1/1/1970" format
    if (typeof dob === 'string' && dob.includes('/')) {
        const parts = dob.split('/');
        if (parts.length === 3) {
            return new Date(parts[2], parts[0] - 1, parts[1]);
        }
    }
    
    // Handle ISO date string
    const date = new Date(dob);
    return isNaN(date.getTime()) ? null : date;
};

/**
 * Parse ISO date string or MongoDB date object
 */
const parseISODate = (dateStr) => {
    if (!dateStr) return null;
    
    // Handle MongoDB $date object
    if (dateStr.$date) {
        const date = new Date(dateStr.$date);
        return isNaN(date.getTime()) ? null : date;
    }
    
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
};

/**
 * Format registration date from timestamp or date object
 */
const formatRegistrationDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Handle MongoDB $date object
    if (timestamp.$date) {
        timestamp = timestamp.$date;
    }
    
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
};

/**
 * Format student groups
 */
const formatStudentGroups = (userInfo) => {
    const groups = [];
    
    // Add grade
    const grade = userInfo['custom:grade'];
    if (grade) {
        groups.push(grade);
    }
    
    // Add board
    const board = userInfo['custom:board'];
    if (board) {
        groups.push(board);
    }
    
    // Add section
    if (userInfo.sectionName) {
        groups.push(userInfo.sectionName);
    }
    
    // Add category name
    if (userInfo.categoryName) {
        groups.push(userInfo.categoryName.toUpperCase());
    }
    
    // Add group if not NO_GROUP
    if (userInfo['custom:group'] && userInfo['custom:group'] !== 'NO_GROUP') {
        groups.push(userInfo['custom:group']);
    }
    
    return groups.length > 0 ? groups : ['No Groups'];
};

/**
 * Format address from multiple fields
 */
const formatAddress = (userInfo) => {
    const parts = [];
    
    // Try addressLine first
    if (userInfo.addressLine) {
        return userInfo.addressLine;
    }
    
    // Build from components
    if (userInfo.cityOrDist) parts.push(userInfo.cityOrDist);
    if (userInfo.state) parts.push(userInfo.state);
    if (userInfo.pinCode) parts.push(`(${userInfo.pinCode})`);
    
    return parts.length > 0 ? parts.join(', ') : '';
};

/**
 * Get default/empty student data structure
 */
export const getDefaultStudentData = () => {
    return {
        firstName: '',
        middleName: '',
        lastName: '',
        enrollmentId: '',
        class: '',
        school: '',
        status: '',
        studentJoinedIn: '',
        gender: '',
        dateOfBirth: null,
        bloodGroup: '',
        category: '',
        refAdmNo: '',
        apptNo: '',
        admissionDate: null,
        registrationDate: '',
        udiseNo: '',
        pointOfContact: '',
        studentMobile: '',
        fatherName: '',
        fatherMobile: '',
        fatherEmail: '',
        motherName: '',
        motherMobile: '',
        motherEmail: '',
        emergencyContact: '',
        studentSegment: '',
        looking: '',
        lastLoginDate: '',
        studentGroups: [],
        transportAvail: 'No',
        transportID: '',
        transportRFID: '',
        staffKid: 'No',
        sitting: 'No',
        biometricCode: '',
        lunchFee: 'NO',
        transportActivities: '',
        currentAddress: '',
        nearestLocality: '',
        apmaarId: '',
        ipinNo: '',
        rfid: ''
    };
};

/**
 * Validate if student data is complete
 */
export const validateStudentData = (studentData) => {
    const requiredFields = [
        'firstName',
        'lastName',
        'enrollmentId',
        'school'
    ];
    
    const missingFields = requiredFields.filter(field => !studentData[field]);
    
    return {
        isValid: missingFields.length === 0,
        missingFields
    };
};

/**
 * Transform attendance data from API response
 * @param {Object} apiResponse - Raw API attendance data
 * @param {String} attendanceType - Type of attendance (regular, rfid, transport, periodwise, professional)
 * @returns {Object} Transformed attendance data with term1, term2, and totals
 */
export const transformAttendanceData = (apiResponse, attendanceType = 'regular') => {
    if (!apiResponse || !apiResponse[attendanceType]) {
        return getDefaultAttendanceData();
    }

    const attendanceData = apiResponse[attendanceType];
    
    return {
        term1: attendanceData.term1 || [],
        term2: attendanceData.term2 || [],
        totals: attendanceData.totals || getDefaultTotals()
    };
};

/**
 * Get default/empty attendance data structure
 */
export const getDefaultAttendanceData = () => {
    return {
        term1: [],
        term2: [],
        totals: getDefaultTotals()
    };
};

/**
 * Get default totals structure
 */
const getDefaultTotals = () => {
    return {
        noOfDays: 0,
        weekoffs: 0,
        holidays: 0,
        workingDays: 0,
        present: 0,
        absent: 0,
        notMarked: 0,
        late: 0,
        percentage: 0
    };
};

/**
 * Transform all attendance types from API response
 * @param {Object} apiResponse - Raw API response containing all attendance types
 * @returns {Object} Object with all attendance types transformed
 */
export const transformAllAttendanceData = (apiResponse) => {
    if (!apiResponse) {
        return {
            regular: getDefaultAttendanceData(),
            rfid: getDefaultAttendanceData(),
            transport: getDefaultAttendanceData(),
            periodwise: getDefaultAttendanceData(),
            professional: getDefaultAttendanceData()
        };
    }

    return {
        regular: transformAttendanceData(apiResponse, 'regular'),
        rfid: transformAttendanceData(apiResponse, 'rfid'),
        transport: transformAttendanceData(apiResponse, 'transport'),
        periodwise: transformAttendanceData(apiResponse, 'periodwise'),
        professional: transformAttendanceData(apiResponse, 'professional')
    };
};
